/**
 * Alphabet stencils — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_lettering`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const alphabetStencilsPack: ToilePack = {
    slug: 'alphabet-stencils',
    category: 'illustration',
    name: 'Alphabet stencils',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'alphabet', label: 'Full alphabet', subject: 'a sheet of the 26 stencil capitals "A" to "Z" in solid black in five rows, each with the small bridges that hold the centers of A, B, D, O, P, Q and R'},
        {key: 'large', label: 'Large letters', subject: 'six large stencil capitals "A" to "F" in a two-by-three grid, solid black, a dashed rectangle around each for cutting'},
        {key: 'sign', label: 'Sign word', subject: 'the word "GARDEN" as one stencil strip across the page in bold military-style stencil capitals with visible bridges, ready to cut'},
    ],
    styles: [
        {key: 'military', label: 'Military', style: 'classic army-style stencil capitals, heavy and squared, with bridges breaking the closed shapes', palette: 'black ink only', framing: 'the chart filling the page inside an even white margin'},
        {key: 'rounded', label: 'Rounded', style: 'soft rounded stencil capitals with wide bridges, friendly and easy to cut', palette: 'black ink only', framing: 'the chart filling the page inside an even white margin'},
        {key: 'slim', label: 'Slim', style: 'tall slim stencil capitals with thin neat bridges, an elegant sign-painter look', palette: 'black ink only', framing: 'the chart filling the page inside an even white margin'},
    ],
    directions: ['lowercase', 'a name', 'numbers'],
    directionHint: 'try: lowercase',
    seo: {
        title: 'Alphabet Stencils: Full Set, Large Letters, Sign Word',
        metaDescription: 'Nine printable letter stencil sheets, A to Z and a sign word, with bridges that hold the inner shapes for a craft knife.',
        h1: 'Alphabet stencils: full A to Z, large letters, a sign word',
        tagline: 'Solid stencil capitals with bridges, in military, rounded and slim cuts.',
        intro: 'Letter stencils are printed, cut out and painted or sprayed through, for signs, crates, walls and shirts. The nine sheets give a full A to Z stencil in five rows, six large capitals A to F in a grid, and the word GARDEN as one stencil strip. Each comes as a military cut, a rounded cut with wide bridges, or a slim sign-painter cut. Ask for lowercase, a name or numbers and the stencil is drawn in that form.',
        useCases: [
            'a sign for a shop or a garden',
            'a crate or box label',
            'a shirt or tote painted through a stencil',
            'a wall lettering project',
        ],
        faq: [
            {q: 'Why do the letters have bridges?', a: 'Closed shapes such as the inside of an A or an O would fall out once cut. The small bridges hold them in place.'},
            {q: 'Are the sheets big enough to cut with a craft knife?', a: 'Yes, the letters are set large enough to cut with a craft knife.'},
            {q: 'What is the difference between the three cuts?', a: 'Military is heavy and squared with bridges breaking the closed shapes. Rounded has soft shapes and wide bridges. Slim is tall and thin, with neat bridges.'},
            {q: 'Can I get a name or numbers?', a: 'Yes. Ask for a name, lowercase or numbers, and the stencil is drawn in that form.'},
        ],
    },
}
